import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Switch, Route, useHistory, useRouteMatch } from 'react-router-dom';
import { find, size, get } from 'lodash';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import ArrowIcon from 'components/Icons/back';
import ReactChildType from 'utils/react-child-type';
import scaleSize from 'utils/scale-size';
import Tab, { TabWrapper } from './Tab';
import Group from './Group';
import {
  StyledTabPanelWrapper,
  StyledTabPanel,
  StyledTabPanelTitle,
  TabPanelItemsWrapper,
  StyledTabPanelContent,
  StyledTabFoldedIcon,
} from './StyledComponents';

const DEFAULT_SECTION_NAME = 'TAB_PANEL';

const TAB_ITEM_HEIGHT = scaleSize(100);

const CONFIG = {
  enterTo: 'last-focused',
  leaveFor: {
    down: '',
    up: '',
    left: '@MAIN_MENU',
  },
};

const DEFAULT_GROUP_ID = 'default_group';

const getTabFromPathname = (tabs, pathname) => {
  return find(tabs, ({ props }) => pathname.includes(props.path));
};

/**
 * It finds the index of the group that currently has focus
 *
 * @param {object[]} groups
 * @param {number} itemIndex
 * @returns {number} - index
 */
const getCurrentGroupIndex = (groups, itemIndex) => {
  // Creates ranges like [[0, 1], [2, 5], [6, 9]]
  const ranges = groups.reduce((r, c, i) => {
    const currMinIndex = get(r, [i - 1, 1]) + 1 || 0;
    const currMaxIndex = currMinIndex + size(c.children) - 1;

    r.push([currMinIndex, currMaxIndex]);
    return r;
  }, []);
  // Returns the index of the range that contains the "itemIndex"
  return ranges.findIndex((r) => r[0] <= itemIndex && r[1] >= itemIndex);
};

const TabPanel = (props) => {
  const { title, groups, defaultTabId, startScrollIndex, viewSize, small, sectionName, id } = props;
  const { isFolded, children, onEnter, onFocus, onUnfoldClick } = props;
  const history = useHistory();
  const match = useRouteMatch();
  const [activeTabId, setActiveTabId] = useState(null);
  const [offset, setOffset] = useState(0);
  const panelDOMRef = useRef(null);

  const grouped = useMemo(() => {
    const childrenArr = React.Children.toArray(children);
    if (!groups) return [{ id: DEFAULT_GROUP_ID, children: childrenArr }];
    return groups.map((group) => {
      return { ...group, children: childrenArr.filter(({ props }) => props.group === group.id) };
    });
  }, [groups, children]);

  /**
   * Because of the nested groups, we do not know the exact number of tabs,
   * but it is needed to correctly calculate the indents when scrolling
   */
  const countOfTabs = useMemo(() => {
    return grouped.reduce((r, { children }) => r + children.length, 0);
  }, [grouped]);

  const onItemEnter = useCallback(
    ({ id, path }) => {
      const route = `${match.url}/${path}`;
      if (route !== history.location.pathname) history.replace(route);
      setActiveTabId(id);
      onEnter && onEnter({ id, path });
    },
    [onEnter, history, match],
  );

  const onItemFocus = useCallback(
    (index) => {
      if (!Number.isInteger(startScrollIndex)) return;
      // Each group has a title as same size as tab item. Need to take this into account.
      // Add "+1" because need to get sum not an index
      const groupTitlesScrolled = getCurrentGroupIndex(grouped, index) + 1;
      // scrolled items + scrolled groups
      const currentStep = index + groupTitlesScrolled;
      const countOfGroups = grouped.length;
      // Max available step to scroll
      const maxStepNumber = countOfGroups + countOfTabs - 1 - Math.floor(viewSize / 2);
      const desiredIndex = currentStep > maxStepNumber ? maxStepNumber : currentStep;
      // We should subtract the first steps regarding "startScrollIndex"
      const offset = Math.max(desiredIndex - startScrollIndex, 0) * TAB_ITEM_HEIGHT;
      // offset should be a negative number
      setOffset(-offset);
    },
    [startScrollIndex, viewSize, countOfTabs, grouped],
  );

  useEffect(() => {
    const childrenArr = React.Children.toArray(children);
    let currentTab = getTabFromPathname(childrenArr, history.location.pathname);

    if (!currentTab && defaultTabId) {
      currentTab = find(childrenArr, ({ props }) => props.id === defaultTabId);
    } else if (!currentTab) {
      currentTab = childrenArr[0];
    }

    if (currentTab) {
      onItemEnter(currentTab.props);
      SpatialNavigationWrapper.focus(`#tab-item-${currentTab.props.id}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * If the number of tabs changes, then you need to re-initialize the navigation completely
   * Because navigation "won't see" new tabs
   */
  useEffect(() => {
    SpatialNavigationWrapper.removeFocusable(sectionName);
    SpatialNavigationWrapper.addFocusable(panelDOMRef.current, {
      config: CONFIG,
      sectionName: sectionName,
    });
    // Need to restore focus on Panel and its active element after unfolding
    // Because on folded state these elements are not exist in DOM
    if (!isFolded) {
      SpatialNavigationWrapper.focus();
      SpatialNavigationWrapper.focus(`#tab-item-${activeTabId}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countOfTabs, sectionName, isFolded]);

  const extraClassName = small ? 'tab-panel__small' : '';

  let realTabIndex = 0;

  return (
    <StyledTabPanelWrapper className={extraClassName} isFolded={isFolded}>
      <StyledTabPanel
        sectionName={sectionName}
        id={id}
        config={CONFIG}
        ref={panelDOMRef}
        offset={offset}
        countOfItems={countOfTabs + grouped.length}
        onFocus={onFocus}
        defaultSection={!isFolded}
      >
        {isFolded && (
          <StyledTabFoldedIcon onEnter={onUnfoldClick}>
            <ArrowIcon />
          </StyledTabFoldedIcon>
        )}
        {!isFolded && (
          <>
            <StyledTabPanelTitle>{title}</StyledTabPanelTitle>
            <TabPanelItemsWrapper countOfItems={countOfTabs + grouped.length}>
              {grouped.map((group, groupIndex) => {
                const tabs = group.children.map((child) => {
                  return (
                    <Tab
                      {...child.props}
                      onEnter={onItemEnter}
                      onFocus={onItemFocus}
                      key={`tab-item-${realTabIndex}`}
                      index={realTabIndex++}
                      isActive={child.props.id === activeTabId}
                    />
                  );
                });
                if (group.id === DEFAULT_GROUP_ID) return tabs;
                return (
                  <Group key={groupIndex} title={group.title}>
                    {tabs}
                  </Group>
                );
              })}
            </TabPanelItemsWrapper>
          </>
        )}
      </StyledTabPanel>
      <StyledTabPanelContent>
        <Switch>
          {React.Children.map(children, (child) => {
            if (typeof child !== 'object') return null;
            const { path, children } = child.props;
            return <Route path={`${match.url}/${path}`}>{children}</Route>;
          })}
        </Switch>
      </StyledTabPanelContent>
    </StyledTabPanelWrapper>
  );
};

TabPanel.Tab = TabWrapper;

TabPanel.defaultProps = {
  viewSize: 8,
  sectionName: DEFAULT_SECTION_NAME,
};

TabPanel.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  groups: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string.isRequired, title: PropTypes.string.isRequired }),
  ),
  defaultTabId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: ReactChildType(TabWrapper),
  startScrollIndex: PropTypes.number,
  viewSize: PropTypes.number,
  sectionName: PropTypes.string,
  id: PropTypes.string,
  onEnter: PropTypes.func,
  onFocus: PropTypes.func,
  onUnfoldClick: PropTypes.func,
  small: PropTypes.bool,
  isFolded: PropTypes.bool,
};

export default TabPanel;
